/**
 * Field analyzer + semantic classifier.
 *
 * `analyzeTree` walks the ingested payload and produces `TSemanticNode`s:
 *  - data type from the value's shape (currency, tag list, long text, …)
 *  - concept from scored keyword lexicons over the key path (never exact
 *    field names, so `burn_multiple`, `esg_score` or `fund_performance`
 *    classify correctly without code changes)
 *  - importance from concept + data type, driving order and disclosure
 */

import type { TConcept, TDataType, TSemanticNode } from "./types";

const LONG_TEXT_THRESHOLD = 220;
const TAG_MAX_LENGTH = 60;

/* ------------------------------------------------------------------ */
/* Key utilities                                                       */
/* ------------------------------------------------------------------ */

export function tokenizeKey(key: string): string[] {
  return key
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

export function humanizeKey(key: string): string {
  const tokens = tokenizeKey(key);
  if (tokens.length === 0) return key;
  const SMALL_WORDS = new Set(["of", "for", "and", "or", "the", "a", "an", "to", "in", "per"]);
  const UPPER_WORDS = new Set(["mrr", "arr", "tam", "sam", "som", "kpi", "roi", "irr", "esg", "gtm", "ai", "api", "url", "id"]);
  return tokens
    .map((t, i) => {
      if (UPPER_WORDS.has(t)) return t.toUpperCase();
      if (i > 0 && SMALL_WORDS.has(t)) return t;
      return t.charAt(0).toUpperCase() + t.slice(1);
    })
    .join(" ");
}

/* ------------------------------------------------------------------ */
/* Concept lexicons                                                    */
/* ------------------------------------------------------------------ */

/**
 * Keyword → concept lexicon. Matching is per-token over the full key path,
 * with the leaf key weighted higher than ancestors.
 */
const CONCEPT_LEXICON: { concept: TConcept; tokens: string[]; weight?: number }[] = [
  { concept: "meta", tokens: ["usage", "tokens", "llm", "cost", "job", "ids", "request", "model", "count", "extracted", "memory"], weight: 2 },
  { concept: "identity", tokens: ["name", "display", "type", "founded", "headquarters", "website", "tagline", "about"] },
  { concept: "thesis", tokens: ["thesis", "philosophy", "strategy", "approach", "mission", "vision", "focus"] },
  { concept: "team", tokens: ["team", "founder", "founders", "ceo", "cto", "employees", "advisors", "hiring", "people"] },
  { concept: "market", tokens: ["market", "tam", "sam", "som", "opportunity", "demand"] },
  { concept: "traction", tokens: ["traction", "customers", "users", "pilots", "engagement", "retention", "signals", "waitlist", "growth"] },
  { concept: "businessModel", tokens: ["business", "model", "pricing", "tiers", "subscription", "monetization"] },
  { concept: "revenue", tokens: ["revenue", "mrr", "arr", "sales", "gmv", "income"] },
  { concept: "financial", tokens: ["burn", "runway", "margin", "cash", "expenses", "budget", "financials", "multiple", "efficiency", "performance", "score"] },
  { concept: "fundraising", tokens: ["raise", "round", "funding", "fundraising", "valuation", "cap", "dilution", "seed", "series"] },
  { concept: "checkSize", tokens: ["check", "ticket", "allocation"] },
  { concept: "idealCustomer", tokens: ["ideal", "icp", "target", "persona", "fit"] },
  { concept: "product", tokens: ["product", "features", "roadmap", "platform", "solution"] },
  { concept: "technology", tokens: ["technology", "tech", "stack", "github", "infrastructure", "patents"] },
  { concept: "moat", tokens: ["moat", "defensibility", "advantage", "differentiation", "ip"] },
  { concept: "risk", tokens: ["risk", "risks", "concerns", "weaknesses", "threats", "exclusions"] },
  { concept: "competition", tokens: ["competition", "competitors", "landscape", "alternatives"] },
  { concept: "portfolio", tokens: ["portfolio", "investments", "deals", "companies", "holdings"] },
  // Weighted so "geo_focus" beats the generic "focus" token in the thesis lexicon
  { concept: "geography", tokens: ["geo", "geography", "geographies", "regions", "location", "countries", "markets"], weight: 2 },
  { concept: "industry", tokens: ["industry", "industries", "sectors", "sector", "verticals", "categories", "keywords", "tags"] },
  { concept: "stage", tokens: ["stage", "stages", "phase", "maturity"] },
  { concept: "valueAdd", tokens: ["value", "add", "support", "services", "help"] },
  { concept: "trackRecord", tokens: ["track", "record", "exits", "notable", "highlights", "history", "past"] },
  { concept: "preferences", tokens: ["preferences", "requirements", "criteria", "must", "haves", "structure", "terms"] },
  { concept: "recommendation", tokens: ["recommendation", "recommendations", "suggestion", "advice", "actions", "next", "steps"] },
  { concept: "facts", tokens: ["truths", "facts", "stored", "evidence", "sources"] },
  { concept: "documents", tokens: ["documents", "files", "deck", "attachments"] },
  { concept: "social", tokens: ["social", "linkedin", "twitter", "links", "media"] },
  { concept: "operations", tokens: ["operations", "ops", "processes", "compliance"] },
  { concept: "governance", tokens: ["governance", "board", "legal", "transparency"] },
];

/** Keys that are internal telemetry regardless of scoring. */
const META_KEY_PATTERN = /^(usage|memory_job_ids?|total_cost|truths_extracted_count|request_id|thread_id|run_id)$/;

export function classifyConcept(path: string[], parentConcept: TConcept | null): TConcept {
  const leaf = path[path.length - 1] ?? "";
  if (META_KEY_PATTERN.test(leaf)) return "meta";

  const leafTokens = new Set(tokenizeKey(leaf));
  const ancestorTokens = new Set(path.slice(0, -1).flatMap(tokenizeKey));

  let best: { concept: TConcept; score: number } | null = null;
  for (const entry of CONCEPT_LEXICON) {
    let score = 0;
    for (const token of entry.tokens) {
      if (leafTokens.has(token)) score += 3 * (entry.weight ?? 1);
      else if (ancestorTokens.has(token)) score += 1 * (entry.weight ?? 1);
    }
    if (score > 0 && (!best || score > best.score)) {
      best = { concept: entry.concept, score };
    }
  }

  if (best) return best.concept;
  // Unmatched children keep their parent's meaning (e.g. thesis.summary)
  if (parentConcept) return parentConcept;
  return "general";
}

/* ------------------------------------------------------------------ */
/* Data type detection                                                 */
/* ------------------------------------------------------------------ */

const MONEY_KEY_TOKENS = new Set([
  "mrr", "arr", "revenue", "burn", "cost", "price", "valuation", "check", "ticket",
  "raise", "funding", "cash", "budget", "amount", "size", "min", "max", "capital",
]);
const PERCENT_KEY_TOKENS = new Set(["pct", "percent", "percentage", "rate", "margin", "growth", "equity", "ownership"]);
const URL_PATTERN = /^https?:\/\/\S+$/i;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}([T ]|$)/;
const ENUM_PATTERN = /^[A-Z][A-Z0-9_]+$/;

function isMoneyKey(path: string[]): boolean {
  return path.some((key) => tokenizeKey(key).some((t) => MONEY_KEY_TOKENS.has(t)));
}

function isPercentKey(key: string): boolean {
  return tokenizeKey(key).some((t) => PERCENT_KEY_TOKENS.has(t));
}

const SENTINEL_VALUES = new Set(["unknown", "n/a", "n/a.", "none", "not provided", "not available"]);

function isEmptyValue(value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length === 0 || SENTINEL_VALUES.has(trimmed.toLowerCase());
  }
  if (Array.isArray(value)) return value.every(isEmptyValue);
  if (typeof value === "object") {
    return Object.values(value as Record<string, unknown>).every(isEmptyValue);
  }
  return false;
}

/** Detect `{min, max, currency?}`-style money ranges without a fixed schema. */
function isCurrencyRange(value: Record<string, unknown>, path: string[]): boolean {
  const keys = Object.keys(value).map((k) => tokenizeKey(k).join(""));
  const hasBound = keys.some((k) => k.includes("min")) || keys.some((k) => k.includes("max"));
  const hasCurrency = keys.some((k) => k.includes("currency"));
  return hasBound && (hasCurrency || isMoneyKey(path));
}

export function detectDataType(value: unknown, path: string[]): TDataType {
  const key = path[path.length - 1] ?? "";

  if (isEmptyValue(value)) return "empty";
  if (typeof value === "boolean") return "boolean";

  if (typeof value === "number") {
    // Money outranks percent for large values: "burn_rate" of 45000 is a
    // currency amount even though "rate" alone suggests a percentage.
    if (isMoneyKey(path) && Math.abs(value) > 100) return "currency";
    if (isPercentKey(key) || (value > 0 && value <= 1 && key.toLowerCase().includes("score"))) return "percentage";
    if (isMoneyKey(path)) return "currency";
    return "number";
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (URL_PATTERN.test(trimmed)) return "url";
    if (EMAIL_PATTERN.test(trimmed)) return "email";
    if (ISO_DATE_PATTERN.test(trimmed)) return "date";
    if (ENUM_PATTERN.test(trimmed) && trimmed.length <= 40) return "enum";
    if (trimmed.length > LONG_TEXT_THRESHOLD) return "longText";
    return "shortText";
  }

  if (Array.isArray(value)) {
    const nonEmpty = value.filter((v) => v != null);
    if (nonEmpty.every((v) => typeof v === "object")) return "objectList";
    if (nonEmpty.every((v) => typeof v === "string" || typeof v === "number")) {
      const allShort = nonEmpty.every((v) => String(v).length <= TAG_MAX_LENGTH);
      return allShort ? "tagList" : "textList";
    }
    return "unknown";
  }

  if (typeof value === "object") {
    if (isCurrencyRange(value as Record<string, unknown>, path)) return "currencyRange";
    return "object";
  }

  return "unknown";
}

/* ------------------------------------------------------------------ */
/* Importance scoring                                                  */
/* ------------------------------------------------------------------ */

const CONCEPT_BASE_IMPORTANCE: Record<TConcept, number> = {
  identity: 100,
  revenue: 92,
  checkSize: 92,
  fundraising: 90,
  financial: 88,
  thesis: 86,
  traction: 84,
  stage: 82,
  industry: 80,
  geography: 78,
  market: 76,
  idealCustomer: 74,
  businessModel: 72,
  product: 70,
  team: 70,
  technology: 66,
  moat: 66,
  portfolio: 64,
  competition: 62,
  valueAdd: 60,
  preferences: 58,
  risk: 56,
  trackRecord: 52,
  recommendation: 50,
  operations: 46,
  governance: 44,
  documents: 42,
  social: 40,
  general: 35,
  facts: 20,
  meta: 0,
};

const DATA_TYPE_BONUS: Partial<Record<TDataType, number>> = {
  currency: 8,
  currencyRange: 8,
  percentage: 6,
  number: 4,
  tagList: 3,
  longText: 2,
};

export function scoreImportance(concept: TConcept, dataType: TDataType, depth: number): number {
  if (concept === "meta" || dataType === "empty") return 0;
  const base = CONCEPT_BASE_IMPORTANCE[concept];
  const bonus = DATA_TYPE_BONUS[dataType] ?? 0;
  return Math.max(0, Math.min(100, base + bonus - depth * 4));
}

/* ------------------------------------------------------------------ */
/* Tree walker                                                         */
/* ------------------------------------------------------------------ */

function analyzeValue(key: string, value: unknown, path: string[], parentConcept: TConcept | null): TSemanticNode {
  const dataType = detectDataType(value, path);
  const concept = classifyConcept(path, parentConcept);
  const node: TSemanticNode = {
    key,
    path,
    label: humanizeKey(key),
    value,
    dataType,
    concept,
    importance: scoreImportance(concept, dataType, path.length - 1),
  };

  if (dataType === "object") {
    node.children = Object.entries(value as Record<string, unknown>).map(([childKey, childValue]) =>
      analyzeValue(childKey, childValue, [...path, childKey], concept)
    );
  }

  return node;
}

/** Analyze every top-level field of an ingested tree. */
export function analyzeTree(tree: Record<string, unknown>): TSemanticNode[] {
  return Object.entries(tree).map(([key, value]) => analyzeValue(key, value, [key], null));
}
